// SPDX-License-Identifier: Apache-2.0

import { ClientJsonRpc, ClientPublicMainnet, ClientPublicTestnet } from "@ckb-ccc/core";
import { ClientPublicDevnet } from "../client/CKBClient";
import config from "../config.json";

/**
 * Single source of truth for which CKB network this build targets, and for
 * everything derived from it.
 *
 * Before this module the choice was spread across five copies of
 *
 *   ckbNetwork === "devnet" ? new ClientPublicDevnet() : new ClientPublicTestnet()
 *
 * which is a binary choice where there are three networks: every one of them
 * silently used the *testnet* system scripts whenever the network was not devnet.
 * On mainnet that is not a wrong label, it is the wrong chain configuration —
 * OmniLock's code hash differs between networks, so script resolution and
 * signing use values that do not exist on the chain being talked to.
 *
 * Anything that varies per network belongs here, so switching networks is one
 * edit rather than a search for every place that assumed two.
 */
export type CKBNetwork = "mainnet" | "testnet" | "devnet";

/**
 * The network this build targets, read from the baked config.
 *
 * This was the literal `"mainnet"`, which made every non-mainnet build wrong in
 * a way no label could reveal. It is not a display setting: it selects the CCC
 * client, the OmniLock code hash and the hub address, so a staging build talked
 * to CKB *mainnet* and resolved scripts against mainnet code hashes while its
 * backend was on testnet. The EVM half already derived its chain from
 * `config.networkIDs` for exactly this reason; this closes the same gap on the
 * CKB half, so one config file decides both chains.
 */
const configured = (config as { ckbNetwork?: string }).ckbNetwork;
if (
  configured !== "mainnet" &&
  configured !== "testnet" &&
  configured !== "devnet"
) {
  throw new Error(
    `configs/*.json must set "ckbNetwork" to "mainnet", "testnet" or "devnet" ` +
      `(got ${JSON.stringify(configured)}) — the CKB client, OmniLock code hash ` +
      `and hub address all derive from it`,
  );
}
export const ckbNetwork: CKBNetwork = configured;

/**
 * Display name for the configured network.
 *
 * Exported so no component hardcodes a network name in its markup. Several did —
 * the onboarding announced "Nervos Testnet" and "Ethereum: Sepolia" as literal
 * text, and the help menu told users to switch MetaMask to Sepolia — which stayed
 * on screen unchanged when the build was pointed at mainnet. A label that cannot
 * disagree with the build is the point.
 */
export const ckbNetworkName: string = {
  mainnet: "Mainnet",
  testnet: "Testnet",
  devnet: "Devnet",
}[ckbNetwork];

/**
 * Creates the CCC client for the configured network.
 *
 * Devnet has no upstream client because its system scripts are deployed locally,
 * which is what `ClientPublicDevnet` supplies from `clientDevnet.ts`.
 */
export function newCKBClient(): ClientJsonRpc {
  switch (ckbNetwork) {
    case "mainnet":
      return new ClientPublicMainnet();
    case "testnet":
      return new ClientPublicTestnet();
    case "devnet":
      return new ClientPublicDevnet();
  }
}

/**
 * OmniLock code hash for the configured network.
 *
 * These are not interchangeable: a lock script built with the wrong code hash
 * refers to a script that is not deployed on the target chain.
 */
export const omniLockCodeHash: string = {
  mainnet: "0x9b819793a64463aed77c615d6cb226eea5487ccfc0783043a587254cda2b6f26",
  testnet: "0xf329effd1c475a2978453c8600e1eaf0bc2087ee093c3ee64cc96ec6847752cb",
  devnet: "0x9c6933d977360f115a3e9cd5a2e0e475853681b80d775d93ad0f8969da343e56",
}[ckbNetwork];

/** Base URL of the CKB block explorer for the configured network. */
export const ckbExplorerBaseURL: string = {
  mainnet: "https://explorer.nervos.org/",
  testnet: "https://testnet.explorer.nervos.org/",
  // The devnet has no hosted explorer; the testnet one at least renders the
  // page rather than failing to resolve.
  devnet: "https://testnet.explorer.nervos.org/",
}[ckbNetwork];

/**
 * The hub's CKB address for the configured network.
 *
 * Mainnet is intentionally empty until the mainnet hub key exists: the address
 * encodes that key, so it cannot be filled in ahead of time. `assertCKBHubAddress`
 * turns the omission into an explicit failure instead of a silent empty string.
 */
export const ckbHubAddress: string = {
  // The hub's OmniLock address, auth flag 0x12, derived from HUB_NERVOS_SK.
  // Must equal what the hub logs at startup as "nervos participant" — if the two
  // disagree the hub rejects every request naming this address.
  mainnet:
    "ckb1qzdcr9un5ezx8tkh03s46m9jymh22jruelq8svzr5krj2nx69dhjvqgj7fzk8h4jv83hjknmjwra4q2dsgzzuvdlqqn5qf23",
  testnet:
    "ckt1qrejnmlar3r452tcg57gvq8patctcgy8acync0hxfnyka35ywafvkqgj3a5grqp6udj2feh8638qe3syjc6jveufqqzz425p",
  devnet:
    "ckt1qzwxjv7ewumq7y2686wdtghqu36c2d5phqxhwhvn458cj6w6xsl9vqgj3a5grqp6udj2feh8638qe3syjc6jveufqqy6ntd3",
}[ckbNetwork];

// A build with no hub address for its own network cannot do anything useful, and
// an empty address would otherwise surface much later as an opaque
// address-decoding failure. Fail at load with something that names the fix.
// The bundler does not execute this, so a mainnet image still builds; it refuses
// to run until the address is filled in.
if (ckbHubAddress === "") {
  throw new Error(
    `no CKB hub address configured for network "${ckbNetwork}" — ` +
      `set ckbHubAddress in src/constants/CKBNetwork.ts from the mainnet hub key`,
  );
}
